import React, { useCallback, useEffect, useMemo, useState } from "react";
import ItemPicker from "@/components/ItemPicker";
import { sortItems } from "@/lib/itemSort";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, Flask, CheckCircle, Warning, ArrowCounterClockwise, ArrowUUpLeft } from "@phosphor-icons/react";
import { istToday } from "@/lib/istDate";

const r2 = (v) => (v == null || Number.isNaN(v) ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const n3 = (v) => (v == null || Number.isNaN(v) ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const today = () => istToday();
const inp = "w-full h-10 bg-slate-800/60 border border-slate-600 text-white rounded-sm px-2 text-sm font-mono-num";
const btn = "h-9 px-3 rounded-sm text-[10px] font-mono-num tracking-widest border transition-colors disabled:opacity-40 flex items-center gap-1.5";
const th = "text-left px-2 py-2";

// Same owner formula as backend _sheet_purchase_calc
export function sheetCalc(item, pieces, kg, rate) {
  if (!item || !(pieces > 0) || !(kg > 0) || !(rate > 0)) return null;
  const size = Number(item.sheet_size_sqft || 32);
  const stock = Math.max(Number(item.current_stock || 0), 0);
  const oldW = Number(item.avg_sheet_weight_kg || item.start_sheet_weight_kg || 0) || null;
  const oldRate = Number(item.avg_rate_per_kg || 0) || null;
  const lotW = kg / pieces;
  const curSheets = stock / size;
  const newW = oldW ? (curSheets * oldW + pieces * lotW) / (curSheets + pieces) : lotW;
  const curKg = oldW ? curSheets * oldW : 0;
  const newRate = oldRate == null ? rate : (curKg * oldRate + kg * rate) / (curKg + kg);
  const total = kg * rate;
  const sqft = pieces * size;
  const warn = oldW && Math.abs(lotW - oldW) / oldW > 0.05
    ? `Is lot ka weight/sheet ${lotW.toFixed(2)} kg average ${oldW.toFixed(2)} kg se ${(Math.abs(lotW - oldW) / oldW * 100).toFixed(1)}% alag hai` : null;
  return { size, total, lotW, sqft, perSheet: total / pieces, perSqft: total / sqft, newRate, newW, newCostSqft: newRate * newW / size, oldRate, oldW, warn };
}

export default function SheetPurchasePanel({ accounts }) {
  const [info, setInfo] = useState({ phase2_applied: false, masters: [] });
  const [suppliers, setSuppliers] = useState([]);
  const [rows, setRows] = useState([]);
  const [returnOf, setReturnOf] = useState(null);
  const load = useCallback(async () => {
    try {
      const [m, s, p] = await Promise.all([api.get("/purchases/sheet/masters"), api.get("/suppliers"), api.get("/purchases/sheet")]);
      setInfo(m.data); setSuppliers(s.data || []); setRows(p.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="surface rounded-sm border-l-4 border-orange-500 p-4 space-y-4" data-testid="sheet-purchase-panel">
      <div>
        <div className="text-[10px] font-mono-num tracking-widest text-orange-300">// SHEET PURCHASE · KG ME · ADMIN ONLY</div>
        <div className="font-heading text-lg font-black text-white">Sheet purchase — sirf master sheets pe</div>
      </div>
      <Phase2Migration applied={info.phase2_applied} onApplied={load} />
      {info.phase2_applied
        ? <SheetPurchaseForm masters={info.masters} suppliers={suppliers} accounts={accounts} onSaved={load} onSupplierAdded={load} />
        : <div className="text-amber-300 text-xs font-mono-num tracking-widest" data-testid="sheet-purchase-locked">PHASE 2 APPLY HONE TAK FORM BAND HAI · PEHLE BACKUP → DRY-RUN → APPLY</div>}
      <SheetPurchaseList rows={rows} onChanged={load} onReturn={setReturnOf} />
      {returnOf && <SheetReturnDialog purchase={returnOf} onClose={() => setReturnOf(null)} onSaved={() => { setReturnOf(null); load(); }} />}
    </div>
  );
}

function Phase2Migration({ applied, onApplied }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");
  const [lastBackup, setLastBackup] = useState(null);
  const loadBackup = useCallback(() => api.get("/admin/backup/log").then(({ data }) => setLastBackup(data?.[0] || null)).catch(() => {}), []);
  useEffect(() => { loadBackup(); }, [loadBackup]);
  const backupFresh = lastBackup && (Date.now() - new Date(lastBackup.at).getTime()) < 24 * 3600 * 1000;
  const backup = async () => {
    setBusy("backup");
    try {
      const { data } = await api.get("/admin/backup/full", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `shobhit-full-backup-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.json.gz`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Full backup downloaded"); loadBackup();
    } catch (_e) { toast.error("Backup fail"); } finally { setBusy(""); }
  };
  const run = async (dry) => {
    if (!dry && !window.confirm("Phase 2 APPLY karein? Sirf 'Sheet purchase' category aur sheet_size_sqft=32 judega.")) return;
    setBusy(dry ? "dry" : "apply");
    try {
      const { data } = await api.post(`/admin/migrations/sheet-purchase?dry_run=${dry}`);
      setReport(data);
      toast.success(dry ? "Phase 2 dry-run ready" : "Phase 2 applied");
      if (!dry) onApplied();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  return (
    <div className="surface-2 p-3 rounded-sm space-y-3" data-testid="phase2-migration">
      <div className="flex flex-wrap gap-2 items-center">
        <span className={`text-[10px] font-mono-num tracking-widest ${applied ? "text-emerald-400" : "text-amber-300"}`} data-testid="phase2-status">
          PHASE 2: {applied ? "APPLIED ✓" : "NOT APPLIED"}
        </span>
        <button onClick={backup} disabled={!!busy} className={`${btn} border-emerald-500/50 text-emerald-300`} data-testid="phase2-backup-btn"><DownloadSimple size={13} /> 1 · FULL BACKUP</button>
        <button onClick={() => run(true)} disabled={!!busy} className={`${btn} border-cyan-500/50 text-cyan-300`} data-testid="phase2-dryrun-btn"><Flask size={13} /> 2 · DRY-RUN</button>
        {!applied && (
          <button onClick={() => run(false)} disabled={!!busy || !backupFresh || !report?.dry_run}
            className={`${btn} border-orange-500 bg-orange-500/20 text-orange-200`} data-testid="phase2-apply-btn"><CheckCircle size={13} /> 3 · APPLY</button>
        )}
        <span className="text-[10px] font-mono-num text-slate-500">LAST BACKUP: {lastBackup ? new Date(lastBackup.at).toLocaleString("en-IN") : "NONE"}</span>
      </div>
      {report && <Phase2Report report={report} />}
    </div>
  );
}

function Phase2Report({ report }) {
  return (
    <div className="space-y-3 text-xs" data-testid="phase2-report">
      <div className={`font-mono-num tracking-widest flex items-center gap-2 ${report.totals_match ? "text-emerald-400" : "text-red-400"}`} data-testid="phase2-totals-match">
        {report.totals_match ? <CheckCircle size={14} /> : <Warning size={14} />}
        {report.dry_run ? "DRY-RUN" : "APPLIED"} · {report.masters_count} MASTER SHEETS · {report.items_to_update} ITEMS GET sheet_size_sqft=32 ·
        CATEGORY "{report.category.label}": {report.category.exists ? "PEHLE SE HAI" : "NAYA BANEGA"} · TOTALS {report.totals_match ? "MATCH ✓" : "MISMATCH ✗"}
      </div>
      {report.reversed_supplier_payments > 0 && (
        <div className="text-amber-300 font-mono-num">{report.reversed_supplier_payments} reversed supplier payment(s) ab supplier due me nahi gine jayenge.</div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
            <tr><th className={th}>MASTER</th><th className={th}>GROUP</th><th className={th}>UNIT</th><th className="text-right px-2">STOCK SQFT</th><th className="text-right px-2">SHEETS</th><th className="text-right px-2">START WT</th><th className={th}>PROBLEMS</th></tr>
          </thead>
          <tbody>
            {report.masters.map((m) => (
              <tr key={m.item_id} className="border-t border-slate-800" data-testid={`phase2-master-${m.item_id}`}>
                <td className="px-2 py-1.5 text-slate-200">{m.name}</td>
                <td className="px-2 py-1.5 font-mono-num text-orange-300">{m.group || "—"}</td>
                <td className="px-2 py-1.5 font-mono-num">{m.unit}</td>
                <td className="px-2 py-1.5 text-right font-mono-num">{n3(m.stock_sqft)}</td>
                <td className="px-2 py-1.5 text-right font-mono-num">{n3(m.stock_sheets)}</td>
                <td className="px-2 py-1.5 text-right font-mono-num">{m.start_sheet_weight_kg ? `${m.start_sheet_weight_kg} KG` : "—"}</td>
                <td className="px-2 py-1.5 text-amber-300">{m.problems.join(" · ") || <span className="text-emerald-400">OK</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <table className="w-full" data-testid="phase2-before-after">
        <tbody>
          {Object.keys(report.before || {}).map((k) => (
            <tr key={k} className="border-t border-slate-800">
              <td className="px-2 py-1 text-slate-400 font-mono-num">{k}</td>
              <td className="px-2 py-1 text-right font-mono-num">{n3(report.before[k])}</td>
              <td className={`px-2 py-1 text-right font-mono-num ${report.before[k] === report.after[k] ? "text-emerald-400" : "text-red-400"}`}>{n3(report.after[k])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const EMPTY = { supplier_id: "", item_id: "", date: today(), pieces: "", weight_kg: "", rate_per_kg: "", paid_now: "", paid_from_account_id: "", invoice_no: "", note: "" };

function SheetPurchaseForm({ masters, suppliers, accounts, onSaved, onSupplierAdded }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const item = masters.find((m) => m.id === f.item_id);
  const c = useMemo(() => sheetCalc(item, Number(f.pieces), Number(f.weight_kg), Number(f.rate_per_kg)), [item, f.pieces, f.weight_kg, f.rate_per_kg]);
  const paid = Number(f.paid_now || 0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const addSupplier = async () => {
    const name = (window.prompt("Naye supplier ka naam") || "").trim();
    if (!name) return;
    try {
      const { data } = await api.post("/suppliers", { name });
      toast.success("Supplier added"); await onSupplierAdded(); setF((x) => ({ ...x, supplier_id: data.id }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    if (!f.supplier_id || !f.item_id || !c) { toast.error("Supplier, sheet, pieces, kg aur rate bhariye"); return; }
    if (paid > 0 && !f.paid_from_account_id) { toast.error("Paid from account chuniye"); return; }
    setBusy(true);
    try {
      await api.post("/purchases/sheet", {
        ...f, pieces: Number(f.pieces), weight_kg: Number(f.weight_kg), rate_per_kg: Number(f.rate_per_kg),
        paid_now: paid, paid_from_account_id: paid > 0 ? f.paid_from_account_id : null,
        idempotency_key: `sp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      toast.success("Sheet purchase saved"); setF({ ...EMPTY, supplier_id: f.supplier_id, paid_from_account_id: f.paid_from_account_id }); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="sheet-purchase-form">
      <div className="grid grid-cols-2 gap-3">
        <L label="Supplier">
          <div className="flex gap-1">
            <select value={f.supplier_id} onChange={set("supplier_id")} className={inp} data-testid="sp-supplier">
              <option value="">Select…</option>
              {suppliers.filter((s) => s.is_active !== false).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button type="button" onClick={addSupplier} className="px-2 border border-slate-600 rounded-sm text-xs text-slate-300" data-testid="sp-add-supplier">+</button>
          </div>
        </L>
        <L label="Date"><input type="date" value={f.date} onChange={set("date")} className={inp} data-testid="sp-date" /></L>
        <L label="Master sheet" full>
          <ItemPicker value={f.item_id} onChange={(v) => setF((x) => ({ ...x, item_id: v }))}
            items={sortItems(masters).map(({ group, ...m }) => m)} placeholder="Select…" testid="sp-item" />
        </L>
        <L label="Pieces (sheets)"><input type="number" min="0" step="1" value={f.pieces} onChange={set("pieces")} className={inp} data-testid="sp-pieces" /></L>
        <L label="Total kg"><input type="number" min="0" step="0.001" value={f.weight_kg} onChange={set("weight_kg")} className={inp} data-testid="sp-kg" /></L>
        <L label="Rate per kg (₹)"><input type="number" min="0" step="0.01" value={f.rate_per_kg} onChange={set("rate_per_kg")} className={inp} data-testid="sp-rate" /></L>
        <L label="Bill / invoice no."><input value={f.invoice_no} onChange={set("invoice_no")} className={inp} data-testid="sp-invoice" /></L>
        <L label="Paid now (₹) — 0 = udhaar"><input type="number" min="0" step="0.01" value={f.paid_now} onChange={set("paid_now")} className={inp} data-testid="sp-paid" /></L>
        <L label="Paid from">
          <select value={f.paid_from_account_id} onChange={set("paid_from_account_id")} className={inp} disabled={!(paid > 0)} data-testid="sp-account">
            <option value="">{paid > 0 ? "Select…" : "—"}</option>
            {(accounts || []).map((a) => <option key={a.id} value={a.id}>{a.account_name}</option>)}
          </select>
        </L>
        <L label="Note" full><input value={f.note} onChange={set("note")} className={inp} data-testid="sp-note" /></L>
        <button onClick={save} disabled={busy || !c} className="col-span-2 h-11 rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-mono-num text-xs tracking-widest disabled:opacity-40" data-testid="sp-save">
          {busy ? "SAVING…" : "SAVE SHEET PURCHASE"}
        </button>
      </div>
      <Preview c={c} item={item} paid={paid} />
    </div>
  );
}

function Preview({ c, item, paid }) {
  const row = (label, value, testid, tone = "text-white") => (
    <div className="flex justify-between border-b border-slate-800 py-1.5">
      <span className="text-slate-400 text-xs">{label}</span>
      <span className={`font-mono-num text-sm ${tone}`} data-testid={testid}>{value}</span>
    </div>
  );
  if (!c) return <div className="surface-2 p-4 rounded-sm text-slate-500 text-sm italic" data-testid="sp-preview">Sheet, pieces, kg aur rate bhariye — live hisaab yahan dikhega.</div>;
  return (
    <div className="surface-2 p-4 rounded-sm" data-testid="sp-preview">
      <div className="text-[10px] font-mono-num tracking-widest text-cyan-300 mb-2">// LIVE PREVIEW · {item?.name}</div>
      {c.warn && <div className="mb-2 p-2 rounded-sm bg-yellow-500/15 border border-yellow-500/60 text-yellow-300 text-xs flex gap-2" data-testid="sp-weight-warning"><Warning size={14} /> {c.warn}</div>}
      {row("Total (kg × rate)", r2(c.total), "sp-total", "text-orange-300")}
      {row("Weight per sheet", `${n3(c.lotW)} kg`, "sp-weight-per-sheet")}
      {row("Sqft added", `${n3(c.sqft)} sqft`, "sp-sqft")}
      {row("Is lot ka cost / sheet", r2(c.perSheet), "sp-cost-sheet")}
      {row("Is lot ka cost / sqft", r2(c.perSqft), "sp-cost-sqft")}
      {row("Purana avg rate / kg", c.oldRate ? r2(c.oldRate) : "— (pehli purchase)", "sp-old-rate")}
      {row("Naya avg rate / kg", r2(c.newRate), "sp-new-rate", "text-emerald-300")}
      {row("Naya avg weight / sheet", `${n3(c.newW)} kg`, "sp-new-weight")}
      {row("Naya avg cost / sqft", r2(c.newCostSqft), "sp-new-cost-sqft", "text-emerald-300")}
      {row("Supplier due (total − paid)", r2(c.total - paid), "sp-due", c.total - paid > 0 ? "text-red-300" : "text-emerald-300")}
    </div>
  );
}

function SheetPurchaseList({ rows, onChanged, onReturn }) {
  const reverse = async (p) => {
    const reason = (window.prompt(`${p.purchase_no} reverse karne ka reason likhiye (stock, payment aur supplier due wapas honge)`) || "").trim();
    if (!reason) return;
    try { await api.post(`/purchases/${p.id}/reverse`, { reason }); toast.success("Purchase reversed"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="overflow-x-auto" data-testid="sheet-purchase-list">
      <div className="text-[10px] font-mono-num tracking-widest text-slate-400 mb-1">// SHEET PURCHASES ({rows.length})</div>
      <table className="w-full text-xs">
        <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
          <tr><th className={th}>DATE</th><th className={th}>NO.</th><th className={th}>SUPPLIER</th><th className={th}>SHEET</th><th className="text-right px-2">KG</th><th className="text-right px-2">RATE</th><th className="text-right px-2">PCS</th><th className="text-right px-2">TOTAL</th><th className="text-right px-2">PAID</th><th className={th}>FROM</th><th className={th}></th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="11" className="text-center py-5 text-slate-500 italic">Abhi koi sheet purchase nahi.</td></tr>}
          {rows.map((p) => (
            <tr key={p.id} className={`border-t border-slate-800 ${p.status === "reversed" ? "opacity-50 line-through" : ""}`} data-testid={`sp-row-${p.id}`}>
              <td className="px-2 py-1.5 font-mono-num">{p.date}</td>
              <td className="px-2 py-1.5 font-mono-num text-orange-300">{p.purchase_no}</td>
              <td className="px-2 py-1.5">{p.supplier_name}</td>
              <td className="px-2 py-1.5">{p.item_name}{p.weight_warning && <Warning size={12} className="inline ml-1 text-yellow-400" />}</td>
              <td className="px-2 py-1.5 text-right font-mono-num">{n3(p.weight_kg)}</td>
              <td className="px-2 py-1.5 text-right font-mono-num">{r2(p.rate_per_kg)}</td>
              <td className="px-2 py-1.5 text-right font-mono-num">{n3(p.pieces)}{p.returned_pieces > 0 && <span className="text-red-300"> (−{n3(p.returned_pieces)})</span>}</td>
              <td className="px-2 py-1.5 text-right font-mono-num">{r2(p.total)}</td>
              <td className="px-2 py-1.5 text-right font-mono-num">{r2(p.paid)}</td>
              <td className="px-2 py-1.5">{p.paid_from || "UDHAAR"}</td>
              <td className="px-2 py-1.5 whitespace-nowrap">
                {p.status === "posted" ? (
                  <span className="flex gap-2 no-underline">
                    <button onClick={() => onReturn(p)} className="text-[10px] font-mono-num text-cyan-300 flex items-center gap-1" data-testid={`sp-return-${p.id}`}><ArrowUUpLeft size={12} /> RETURN</button>
                    <button onClick={() => reverse(p)} className="text-[10px] font-mono-num text-red-300 flex items-center gap-1" data-testid={`sp-reverse-${p.id}`}><ArrowCounterClockwise size={12} /> REVERSE</button>
                  </span>
                ) : <span className="text-[10px] font-mono-num text-red-300">REVERSED</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SheetReturnDialog({ purchase, onClose, onSaved }) {
  const [f, setF] = useState({ date: today(), pieces: "", weight_kg: "", rate_per_kg: purchase.rate_per_kg, reason: "" });
  const [busy, setBusy] = useState(false);
  const amount = Number(f.weight_kg || 0) * Number(f.rate_per_kg || 0);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/purchases/${purchase.id}/sheet-return`, {
        ...f, pieces: Number(f.pieces), weight_kg: Number(f.weight_kg), rate_per_kg: Number(f.rate_per_kg),
        idempotency_key: `sr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      toast.success("Return saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="sheet-return-dialog">
      <div className="surface rounded-sm p-5 w-full max-w-md space-y-3">
        <div className="font-heading font-black text-white">Return · {purchase.purchase_no} · {purchase.item_name}</div>
        <div className="text-[10px] font-mono-num text-slate-500">BACHE HUE: {n3(purchase.pieces - purchase.returned_pieces)} PCS · {n3(purchase.weight_kg - purchase.returned_kg)} KG</div>
        <div className="grid grid-cols-2 gap-3">
          <L label="Date"><input type="date" value={f.date} onChange={set("date")} className={inp} data-testid="sr-date" /></L>
          <L label="Pieces"><input type="number" value={f.pieces} onChange={set("pieces")} className={inp} data-testid="sr-pieces" /></L>
          <L label="Kg"><input type="number" value={f.weight_kg} onChange={set("weight_kg")} className={inp} data-testid="sr-kg" /></L>
          <L label="Rate / kg"><input type="number" value={f.rate_per_kg} onChange={set("rate_per_kg")} className={inp} data-testid="sr-rate" /></L>
          <L label="Reason" full><input value={f.reason} onChange={set("reason")} className={inp} data-testid="sr-reason" /></L>
        </div>
        <div className="text-sm text-slate-300">Supplier due kam hoga: <b className="text-orange-300 font-mono-num" data-testid="sr-amount">{r2(amount)}</b> · Stock −{n3(Number(f.pieces || 0) * 32)} sqft</div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-4 border border-slate-600 rounded-sm text-xs text-slate-300" data-testid="sr-cancel">CANCEL</button>
          <button onClick={save} disabled={busy || !(Number(f.pieces) > 0) || !(Number(f.weight_kg) > 0)} className="h-9 px-4 bg-cyan-600 rounded-sm text-xs text-white disabled:opacity-40" data-testid="sr-save">SAVE RETURN</button>
        </div>
      </div>
    </div>
  );
}

export function SheetReturnsList() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/purchases/sheet-returns").then(({ data }) => setRows(data || [])).catch(() => {}); }, []);
  return (
    <div className="surface p-3 rounded-sm border-l-4 border-cyan-500/60 mb-4 overflow-x-auto" data-testid="sheet-returns-list">
      <div className="text-[10px] font-mono-num tracking-widest text-cyan-300 mb-2">// SHEET PURCHASE RETURNS ({rows.length})</div>
      <table className="w-full text-xs">
        <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
          <tr><th className={th}>DATE</th><th className={th}>RETURN NO.</th><th className={th}>PURCHASE</th><th className={th}>SUPPLIER</th><th className={th}>SHEET</th><th className="text-right px-2">PCS</th><th className="text-right px-2">KG</th><th className="text-right px-2">RATE</th><th className="text-right px-2">AMOUNT</th><th className={th}>BY</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="10" className="text-center py-4 text-slate-500 italic">Koi sheet return nahi.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-800" data-testid={`sr-row-${r.id}`}>
              <td className="px-2 py-1.5 font-mono-num">{r.date}</td>
              <td className="px-2 py-1.5 font-mono-num text-cyan-300">{r.return_no}</td>
              <td className="px-2 py-1.5 font-mono-num">{r.purchase_no}</td>
              <td className="px-2 py-1.5">{r.supplier_name}</td>
              <td className="px-2 py-1.5">{r.item_name}</td>
              <td className="px-2 py-1.5 text-right font-mono-num">{n3(r.pieces)}</td>
              <td className="px-2 py-1.5 text-right font-mono-num">{n3(r.weight_kg)}</td>
              <td className="px-2 py-1.5 text-right font-mono-num">{r2(r.rate_per_kg)}</td>
              <td className="px-2 py-1.5 text-right font-mono-num">{r2(r.total)}</td>
              <td className="px-2 py-1.5">{r.recorded_by_name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function L({ label, children, full }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <span className="block text-[10px] font-mono-num tracking-widest text-slate-400 mb-1 uppercase">{label}</span>
      {children}
    </label>
  );
}
